import React, { useState, useEffect, useCallback, memo } from "react";
import { NavLink } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Toolbar,
  useMediaQuery,
  Typography,
  Popover,
  MenuItem,
  MenuList,
  Paper,
} from "@mui/material";
import { Menu as MenuIcon, ChevronRight } from "@mui/icons-material";
import logo from "../images/BPMC_Logo_White.png";

const checkAuthorization = (path) => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (!user || !user.access) return false;

  const accessList = Object.values(user.access).flat();

  const pathMatch = accessList.some((route) => {
    try {
      const routeRegex = new RegExp(
        `^${route.replace(/:[^\s/]+/g, "([^/]+)")}$`
      );
      return routeRegex.test(path);
    } catch (err) {
      console.error("Error creating regex:", err.message);
    }
  });

  return pathMatch;
};

const links = [
  { to: "/", icon: "apps", label: "Main Menu", alwaysVisible: true },
  {
    title: "Operations",
    icon: "settings",
    items: [
      { to: "/view-calendar", icon: "calendar_today", label: "Calendar" },
      { to: "/manage-job-orders", icon: "work", label: "Job Orders" },
      { to: "/view-daily-forecast", icon: "wb_twilight", label: "Daily Forecast" },
      { to: "/assign-technicians", icon: "assignment_ind", label: "Technician Assignment" },
      {
        to: "/manage-endorsement-form",
        icon: "fact_check",
        label: "Endorsement Forms",
      },
      {
        to: "/manage-service-reports",
        icon: "receipt_long",
        label: "Service Reports",
      },
      {
        to: "/view-productivity-report",
        icon: "trending_up",
        label: "Productivity Report",
      },
      {
        to: "/view-operation-completion-report",
        icon: "task_alt",
        label: "Operation Completion Report",
      },
      {
        to: "/view-open-job-orders-report",
        icon: "pending_actions",
        label: "Open Job Orders Report",
      },
      {
        to: "/create-accomplishment-reports",
        icon: "note_add",
        label: "Termite Accomplishment Report",
      },
      { to: "/manage-technicians", icon: "engineering", label: "Technicians" },
      {
        to: "/view-competency-skills-matrix",
        icon: "grid_view",
        label: "Competency Skills Matrix",
      },
      {
        to: "/view-professional-skills-matrix",
        icon: "school",
        label: "Professional Skills Matrix",
      },
      { to: "/view-rest-day", icon: "weekend", label: "Rest Day" },
    ],
  },
  {
    title: "Billing",
    icon: "trending_up",
    items: [
      // { to: "/manage-leads", icon: "track_changes", label: "Leads" },
      // {
      //   to: "/manage-salesreps",
      //   icon: "supervisor_account",
      //   label: "Sales Reps",
      // },
      // {
      //   to: "/view-lead-status-report",
      //   icon: "analytics",
      //   label: "Lead Status Report",
      // },
      {
        to: "/manage-contracts",
        icon: "receipt_long",
        label: "Manage Contracts",
      },
      {
        to: "/contracts-view",
        icon: "fact_check",
        label: "View Contracts",
      },
      {
        to: "/sales-report",
        icon: "bar_chart",
        label: "Sales Report",
      },      
    ],
  },
  {
    title: "Client Retention",
    icon: "people",
    items: [
      {
        to: "/manage-clients",
        icon: "supervised_user_circle",
        label: "Clients",
      },
      {
        to: "/view-client-service-reports",
        icon: "article",
        label: "Client Service Reports",
      },
      // {
      //   to: "/send-job-order-reminder",
      //   icon: "alarm",
      //   label: "Job Order Reminders",
      // },
      { to: "/send-promotions", icon: "campaign", label: "Promotions" },
      { to: "/list-email-exceptions", icon: "block", label: "Email Exceptions" }
    ],
  },
  {
    title: "Payroll",
    icon: "payments",
    items: [
      { to: "/payroll-reports", icon: "payments", label: "Payroll Reports", },
      { to: "/technician-hours", icon: "schedule", label: "Technician Hours", },
      { to: "/technician-compensation", icon: "attach_money", label: "Technician Compensation", }
    ]
  },
  {
    title: "Admin Hub",
    icon: "admin_panel_settings",
    items: [
      { to: "/manage-users", icon: "admin_panel_settings", label: "Users" },
      { to: "/manage-access", icon: "vpn_key", label: "Access" },
      { to: "/delete-technicians", icon: "person_remove", label: "Delete Technicians" },
      { to: "/delete-users", icon: "no_accounts", label: "Delete Users" }
    ],
  },
  { to: "/logout", icon: "logout", label: "Logout", alwaysVisible: true },
];

function Sidebar() {
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 1024);
  const [openSections, setOpenSections] = useState({});
  const isSmallScreen = useMediaQuery("(max-width: 1024px)");
  const isVerySmallScreen = useMediaQuery("(max-width: 600px)");

  const toggleSidebar = useCallback(() => {
    setCollapsed((prevState) => !prevState);
  }, []);

  const handleResize = useCallback(() => {
    setCollapsed(window.innerWidth <= 768);
  }, []);

  // Close popover when clicking outside the sidebar - much simpler approach
  useEffect(() => {
    const handleDocumentClick = (event) => {
      // If we're clicking on a section header, the section click handler will deal with it
      if (event.target.closest('[data-section-header]')) {
        return;
      }
      
      // If we're clicking outside the drawer entirely, close all sections
      const drawer = event.target.closest('.MuiDrawer-root');
      const popover = event.target.closest('.MuiPopover-root');
      
      if (!drawer && !popover) {
        setOpenSections({});
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  const handleSectionClick = (event, section) => {
    event.stopPropagation();
    event.preventDefault();
    
    const sectionTitle = section.title;
    const anchorElement = event.currentTarget;
    
    setOpenSections(prev => {
      // Check if this exact section is currently open
      const isCurrentlyOpen = prev[sectionTitle]?.open;
      
      // If clicking the same section, toggle it off
      if (isCurrentlyOpen) {
        return {};
      }
      
      // Open the new section (replacing any other open section)
      return {
        [sectionTitle]: {
          open: true,
          anchorEl: anchorElement
        }
      };
    });
  };

  const handlePopoverClose = (sectionTitle) => {
    setOpenSections({});
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const listItemIconStyle = {
    minWidth: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    cursor: "pointer"
  };

  return (
    <>
      <Drawer
        variant="permanent"
        open={!collapsed}
        anchor={isVerySmallScreen ? "top" : "left"}
        data-sidebar
        sx={{
          width: collapsed ? 54 : 280,
          flexShrink: 0,
          transition: "width 0.3s ease",
          "& .MuiDrawer-paper": {
            width: collapsed ? 54 : 280,
            height: "100vh",
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            boxSizing: "border-box",
            overflowX: "hidden",
            overflowY: "auto",
            transition: "width 0.3s ease",
            backgroundColor: "#1E3F4F",
            color: "white",
            padding: 1,
            zIndex: 1200,
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#1e3f4f",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#4f6d7a",
              borderRadius: "10px",
              border: "2px solid #1e3f4f",
            },
            // Mobile specific styles
            [isVerySmallScreen ? '@media (max-width: 600px)' : '']: {
              height: "100vh",
              minHeight: "100vh",
              maxHeight: "100vh",
            },
          },
        }}
      >
        <List sx={{ flex: 1, paddingBottom: 2 }}>
          {/* Menu Toggle and Logo */}
          <ListItem
            sx={{
              justifyContent: collapsed ? "center" : "flex-start",
              marginTop: 1,
              minHeight: 64,
              paddingRight: 0,
            }}
          >
            <ListItemIcon onClick={toggleSidebar} sx={listItemIconStyle}>
              <span className="material-icons">
                <MenuIcon />
              </span>
            </ListItemIcon>
            {!collapsed && (
              <Box
                component="img"
                src={logo}
                alt="Pest Control Logo"
                sx={{
                  height: { xs: 40, sm: 40 },
                  width: "auto",
                  maxWidth: "100%",
                }}
              />
            )}
          </ListItem>

          {links.map((link) => {
            // Main Menu and Logout items
            if (link.alwaysVisible || checkAuthorization(link.to)) {
              return (
                <ListItem
                  key={link.to}
                  component={NavLink}
                  to={link.to}
                  exact="true"
                  sx={{
                    justifyContent: collapsed ? "center" : "flex-start",
                    color: "white",
                    "&.Mui-selected": {
                      backgroundColor: "transparent",
                    },
                    paddingRight: 0,
                  }}
                >
                  <ListItemIcon sx={listItemIconStyle}>
                    <span className="material-icons">{link.icon}</span>
                  </ListItemIcon>
                  {!collapsed && link.label && (
                    <ListItemText
                      primary={link.label}
                      sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "white",
                      }}
                    />
                  )}
                </ListItem>
              );
            }

            // Section headers with dropdown
            if (link.title) {
              const authorizedItems = link.items.filter((item) =>
                item.alwaysVisible || checkAuthorization(item.to)
              );

              if (authorizedItems.length === 0) return null;

              return (
                <ListItem
                  key={link.title}
                  data-section-header
                  onClick={(event) => handleSectionClick(event, link)}
                  sx={{
                    justifyContent: collapsed ? "center" : "flex-start",
                    color: "white",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    paddingRight: 0,
                  }}
                >
                  <ListItemIcon sx={listItemIconStyle}>
                    <span className="material-icons">{link.icon}</span>
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "white",
                          }}
                        >
                          {link.title}
                        </Typography>
                      }
                      sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    />
                  )}
                  {!collapsed && (
                    <ChevronRight sx={{ color: "white", marginLeft: "auto" }} />
                  )}
                </ListItem>
              );
            }
            return null;
          })}
        </List>
      </Drawer>

      {/* Individual Popovers for each section */}
      {links.map((link) => {
        if (!link.title) return null;
        
        const authorizedItems = link.items.filter((item) =>
          item.alwaysVisible || checkAuthorization(item.to)
        );

        if (authorizedItems.length === 0) return null;

        const sectionState = openSections[link.title] || { open: false, anchorEl: null };

        return (
          <Popover
            key={link.title}
            open={sectionState.open}
            anchorEl={sectionState.anchorEl}
            onClose={(event, reason) => {
              // Allow both backdrop clicks and escape key to close
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                setOpenSections({});
              }
            }}
            anchorOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#2C5F6F",
                color: "white",
                minWidth: 200,
              },
            }}
            transitionDuration={0}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
          >
            <Paper sx={{ backgroundColor: "#2C5F6F" }}>
              <MenuList>
                {authorizedItems.map((item) => (
                  <MenuItem
                    key={item.to}
                    component={NavLink}
                    to={item.to}
                    onClick={() => {
                      handlePopoverClose(link.title);
                    }}
                    sx={{
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <span className="material-icons" style={{ fontSize: 20 }}>
                      {item.icon}
                    </span>
                    {item.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </Popover>
        );
      })}
    </>
  );
}

export default memo(Sidebar);